import React from 'react';
import { useTranslation } from 'react-i18next';
import { withRouter, RouteComponentProps } from 'react-router';
import moment from 'moment';
import { Popup } from 'semantic-ui-react';
import { connect } from 'react-redux';
import Profile from '@common/components/profile';
import Card from '@common/components/card';
import Icon from '@common/components/icon';
import Dropdown from '@common/components/dropdown';
import ConfirmButton from '@common/components/confirm-button';
import { EParentTypes } from '@common/definitions';
import type { LoggedUser, SimpleUser } from '@common/types/objects';
import type { Message } from '../../../../types/objects';
import ProfileImage from '@common/components/profile-image';
import { PopoverContent } from '@common/components/profile/popover';

const defaultUser: SimpleUser = {
  id: '',
  full_name: '"Verwijderde gebruiker"',
  profile_img: null,
  last_active: null,
};

type Props = RouteComponentProps & {
  loggedUser: LoggedUser;
  item: Message;
  shouldHideParent: boolean;
  onDelete: (id: string) => Promise<void>;
  onToggleImportant: (id: string, important: boolean) => Promise<void>;
  onToggleComments: (messageId: string, enabled: boolean) => Promise<void>;
  onToggleMute: (id: string) => Promise<void>;
  onMarkAsRead: () => void;
  network?: string | null;
};

const DefaultMessageContentComponent = ({
  item,
  loggedUser,
  shouldHideParent,
  onMarkAsRead,
  onToggleImportant,
  onToggleComments,
  onToggleMute,
  onDelete,
  network,
  history,
  match,
}: Props) => {
  const { t } = useTranslation();
  const { user, recognition } = item;

  const showDropdown = item.actions?.can_edit
    || item.actions?.can_mute
    || item.actions?.can_mark_important
    || item.actions?.can_delete;

  const isChannel = match.path === '/networks/:networkId/messages/:type/:id' &&
    // @ts-expect-error
    match?.params?.type === 'channel';


  return (
    <div className="Message__Inner Message--type-default">
      <Card.Header>
        <div className="Message__header__inner">
          <div className="pull-right">
            {item.is_important && <Icon type="star__filled" className="Message__Important" />}
            {showDropdown && (
              <Dropdown
                alignRight
                id={`Message__Actions-${item.id}`}
                toggle={<Icon type="more_vert" size="small" />}
              >
                {item.actions.can_edit && item.message_type !== 'recognition_message' && (
                  <Dropdown.Item>
                    {t('social:feed_item_message_edit_message')}
                  </Dropdown.Item>
                )}
                {item.is_important && !item.has_read && (
                  <Dropdown.Item onClick={onMarkAsRead}>
                    {t('social:feed_item_message_mark_as_read')}
                  </Dropdown.Item>
                )}
                {!item.publish_at && item.actions.can_mute && (
                  <Dropdown.Item onClick={() => onToggleMute(item.id)}>
                    {!item.has_muted
                      ? t('social:feed_item_message_mute')
                      : t('social:feed_item_message_unmute')}
                  </Dropdown.Item>
                )}
                {!item.publish_at && item.actions.can_mark_important && (item.is_important ? (
                  <ConfirmButton
                    danger
                    key="remove"
                    title={t('social:feed_item_message_default_confirm_remove_important_title')}
                    onConfirm={() => onToggleImportant(item.id, false)}
                  >
                    <Dropdown.Item>{t('social:feed_item_message_remove_important')}</Dropdown.Item>
                  </ConfirmButton>
                ) : (
                  <ConfirmButton
                    key="mark"
                    title={t('social:feed_item_message_default_confirm_mark_as_important_title')}
                    onConfirm={() => onToggleImportant(item.id, true)}
                  >
                    <Dropdown.Item>{t('social:feed_item_message_mark_as_important')}</Dropdown.Item>
                  </ConfirmButton>
                ))}
                {item.actions.can_disable_comments && (item.can_comment ? (
                  <ConfirmButton
                    danger
                    key="disable_comments"
                    title={t('social:feed_item_message_default_confirm_disable_comments_title')}
                    onConfirm={() => onToggleComments(item.id, false)}
                  >
                    <Dropdown.Item>{t('social:feed_item_message_disable_comments')}</Dropdown.Item>
                  </ConfirmButton>
                ) : (
                  <ConfirmButton
                    key="enable_comments"
                    title={t('social:feed_item_message_default_confirm_enable_comments_title')}
                    onConfirm={() => onToggleComments(item.id, true)}
                  >
                    <Dropdown.Item>{t('social:feed_item_message_enable_comments')}</Dropdown.Item>
                  </ConfirmButton>
                ))}
                {item.actions.can_delete && (
                  <ConfirmButton
                    danger
                    title={t('social:feed_item_message_default_confirm_remove_title')}
                    description={t('social:feed_item_message_default_confirm_remove_description')}
                    onConfirm={() => onDelete(item.id)}
                    confirmText={t('social:feed_item_message_default_confirm_remove')}
                  >
                    <Dropdown.Item danger>{t('social:feed_item_message_default_confirm_remove')}</Dropdown.Item>
                  </ConfirmButton>
                )}
              </Dropdown>
            )}
          </div>

          <Profile
            user={user}
            meta={!shouldHideParent && (
              <>
                <Icon type="chevron_right" />
                <span>{item.created_in?.values}</span>
              </>
            )}
            enablePopover={item.user_id !== loggedUser.id && !isChannel}
          >
            <div>
              <small className={item.publish_at && 'Message__PublishAt'}>
                <Icon key="icon" type="access_time" />
                {item.publish_at ? (
                  t('social:feed_item_message_default_planned_at', {
                    datetime: moment(item.publish_at).format(`dddd D MMMM [${t('social:feed_item_message_default_at')}] LT`),
                  })
                ) : moment(item.created_at).calendar()}
              </small>
              {item.created_in.type === EParentTypes.EVENT && (
                <>
                  <span>·</span>
                  <small>
                    <Icon key="icon" type="event" />
                    {t('social:feed_item_message_event')}
                  </small>
                </>
              )}
            </div>
          </Profile>
        </div>
      </Card.Header>

      <div className="Message__content fs-exclude">
        <Card containerClassName="Recognition__Card tw-p-6">
          <Card.Header className="Recognition__Card__Header">
            <div className="Recognition__Card__Profile">
              <div>
                <ProfileImage
                  size={40}
                  user={recognition && recognition.receiver ? recognition.receiver : {}}
                  enablePopover={recognition && recognition.receiver.id !== loggedUser.id && !isChannel}
                  className="tw-rounded-full"
                />
              </div>
              <div>
                <Icon type="highfive__filled" color="#111827" />
              </div>
            </div>
            <div className="tw-flex tw-flex-col">
              <p className="tw-m-0 tw-text-[13px]">{t('social:recognition_send_message')}</p>
              <Popup
                className="popover ProfilePopover"
                trigger={
                  <div className="tw-cursor-pointer">
                    <p className="tw-text-base tw-m-0 tw-font-medium">
                      {recognition?.receiver?.full_name}
                    </p>
                  </div>
                }
                flowing
                hoverable
                mouseEnterDelay={500}
                mouseLeaveDelay={500}
                position="top left"
                disabled={isChannel}
              >
                <PopoverContent
                  user={recognition?.receiver || defaultUser}
                  loggedUserId={loggedUser.id}
                  network={network}
                  history={history}
                  match={match}
                />
              </Popup>
            </div>
          </Card.Header>
          <Card.Content className="Recognition__Card__Content tw-flex tw-flex-col tw-gap-4 tw-p-0">
            <p className="tw-text-2xl tw-m-0 tw-font-playfair tw-font-medium tw-text-white tw-leading-snug">
              “{recognition && recognition.text ? recognition.text : ''}”
            </p>
            <div className="Recognition__Card_Footer">
              {recognition && recognition.values && recognition.values.map((value) => (
                <div className="Recognition__Card_Footer_Tag tw-bg-[rgba(255,255,255,0.1)]" key={value.id}>{value.name}</div>
              ))}
            </div>
          </Card.Content>
        </Card>
      </div>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  network: state.network.selected,
});

export default connect(mapStateToProps)(withRouter(DefaultMessageContentComponent));
